import { useCallback, useState } from "react";

import {
  type ApiServiceBaseOperationDescriptor,
  type CloudFlowNodeType,
  CloudFlowProvider,
  type Member,
  ModelType,
} from "@doitintl/cmp-models";
import { type WithModel } from "@doitintl/models-any";
import { Stack } from "@mui/material";

import { Loader } from "../../../../../Components/Loader";
import LoadingButton from "../../../../../Components/LoadingButton";
import { ApiActionParametersForm } from "../../ApiActionParametersForm/ApiActionParametersForm";
import AWSPermissionForm from "../../ApiActionParametersForm/AWSPermissionForm";
import GCPPermissionForm from "../../ApiActionParametersForm/GCPPermissionForm";
import { useGetOperationById } from "../../Common/hooks/useGetOperationById";
import { useUnwrappedApiActionModel } from "../../Common/hooks/useUnwrappedApiActionModel";
import { GCPHierarchicalResource } from "../../utils/gcpUtils";
import { type PermissionConfigValues, useCloudPermissions } from "../hooks";
import { useNodeConfigurationContext } from "../NodeConfigurationContext";
import { PermissionCheckResults } from "./PermissionCheckResults";

const GcpPermissionFormWrapper = ({
  operation,
  permissionFormValues,
  setConfigurationFormValid,
  onConfigurationValuesChange,
  isOperationLoading,
  modelId,
  resetPermissions,
}: {
  operation: WithModel<ApiServiceBaseOperationDescriptor> | null;
  permissionFormValues: object;
  setConfigurationFormValid: (valid: boolean) => void;
  onConfigurationValuesChange: (values: unknown) => void;
  isOperationLoading: boolean;
  modelId: string | undefined;
  resetPermissions: () => void;
}) => {
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const isGCPProvider = nodeConfig.parameters.operation.provider === CloudFlowProvider.GCP;

  const permissionsFormModel = operation?.parameters && {
    ...operation.parameters,
    members: {
      ...operation.parameters.members,
      ...(isGCPProvider && {
        permissionsProjectId: {
          model: { type: ModelType.STRING },
        } satisfies Member,
        hierarchicalResource: {
          model: {
            type: ModelType.STRING,
            enum: [GCPHierarchicalResource.ORGANIZATION, GCPHierarchicalResource.PROJECT],
          },
        } satisfies Member,
      }),
    },
    requiredMembers: isGCPProvider
      ? ["serviceAccountResource", "hierarchicalResource"]
      : operation.parameters.requiredMembers,
  };

  return (
    <Loader loading={isOperationLoading || modelId !== operation?.inputModel}>
      {permissionsFormModel && (
        <ApiActionParametersForm
          key={nodeConfig.id}
          inputModel={permissionsFormModel}
          values={permissionFormValues}
          onValidityChange={setConfigurationFormValid}
          onValuesChange={onConfigurationValuesChange}
        >
          <GCPPermissionForm resetPermissions={resetPermissions} inputModel={permissionsFormModel} />
        </ApiActionParametersForm>
      )}
    </Loader>
  );
};

const PermissionsTab = () => {
  const { nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();

  const { configurationValues } = nodeConfig.parameters;

  const [permissionFormValues, setPermissionFormValues] = useState<PermissionConfigValues>(configurationValues);

  const { result, loading, updatePermissions, resetPermissions } = useCloudPermissions({
    permissionConfigValues: permissionFormValues,
  });

  const {
    operationData: { operation, operationPointer },
    loading: isOperationLoading,
  } = useGetOperationById(nodeConfig.parameters.operation);
  const { modelId } = useUnwrappedApiActionModel(operationPointer, operation?.inputModel);
  const [configurationFormValid, setConfigurationFormValid] = useState<boolean>(true);

  const onConfigurationValuesChange = useCallback((newValues: unknown) => {
    setPermissionFormValues(newValues as PermissionConfigValues);
  }, []);

  return (
    <Stack spacing={2} p={2} alignItems="flex-start">
      {nodeConfig.parameters?.provider === "AWS" && <AWSPermissionForm />}
      {nodeConfig.parameters?.provider === "GCP" && (
        <GcpPermissionFormWrapper
          resetPermissions={resetPermissions}
          operation={operation}
          permissionFormValues={{ ...permissionFormValues, hierarchicalResource: GCPHierarchicalResource.ORGANIZATION }}
          setConfigurationFormValid={setConfigurationFormValid}
          onConfigurationValuesChange={onConfigurationValuesChange}
          isOperationLoading={isOperationLoading}
          modelId={modelId}
        />
      )}
      <LoadingButton
        variant="outlined"
        onClick={updatePermissions}
        loading={loading}
        disabled={!configurationFormValid}
        mixpanelEventId="cloudflow.check-permissions"
        sx={{ alignSelf: "flex-start" }}
      >
        Check for required permissions
      </LoadingButton>
      <PermissionCheckResults
        nodeConfig={nodeConfig}
        permissionFormValues={permissionFormValues}
        loading={loading}
        result={result}
      />
    </Stack>
  );
};

export default PermissionsTab;
